import { API_URL } from "../config";

/** Typed client for the oab-ledger read endpoints (api/ledger_routes.py). */

export interface LedgerStatus {
  available: boolean;
  backend?: "sqlite" | "postgres";
  seeded?: boolean;
  asset_count?: number;
  db_path?: string;
  reason?: string;
}

export interface LedgerCustomer {
  customer_id: string;
  asset_count: number;
}

export interface LedgerAssetTypeCount {
  asset_type: string;
  count: number;
}

export interface LedgerAsset {
  asset_id: string;
  customer_id: string;
  asset_type: string;
  created_at: string | null;
  last_seen_source_id: string | null;
  last_seen_at: string | null;
  consecutive_misses: number;
  ifc_guid: string | null;
}

export interface LedgerObservation {
  history_id: number;
  asset_id: string;
  attribute_name: string;
  value: unknown;
  confidence: number | null;
  quality_level: string | null;
  source_id: string;
  recorded_at: string | null;
  observed_at: string | null;
  is_current: boolean;
  override_of: number | null;
  notes: string | null;
}

export interface LedgerSource {
  source_id: string;
  customer_id: string;
  source_type: string;
  source_name: string;
  source_date: string | null;
  uploaded_at: string | null;
  uploaded_by: string | null;
  audit_score: number | null;
  scope: string;
  revoked: boolean;
}

/** A row from review_observations — current obs joined to source + derivation. */
export interface LedgerReviewRow {
  asset_id: string;
  asset_type: string;
  attribute: string;
  value: unknown;
  confidence: number | null;
  quality_level: string | null;
  notes: string | null;
  override_of: number | null;
  source_name: string;
  source_type: string;
  derivation_method: string | null;
  derivation_params: unknown;
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`);
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return res.json();
}

export const getLedgerStatus = () => getJSON<LedgerStatus>("/api/ledger/status");

export const listCustomers = () =>
  getJSON<{ customers: LedgerCustomer[] }>("/api/ledger/customers").then((d) => d.customers);

export const listAssetTypes = (customerId: string) =>
  getJSON<{ asset_types: LedgerAssetTypeCount[]; total: number }>(
    `/api/ledger/customers/${encodeURIComponent(customerId)}/asset-types`
  );

export const listLedgerAssets = (
  customerId: string,
  opts: { type?: string; page?: number; pageSize?: number } = {}
) => {
  const p = new URLSearchParams();
  if (opts.type) p.set("type", opts.type);
  if (opts.page) p.set("page", String(opts.page));
  if (opts.pageSize) p.set("page_size", String(opts.pageSize));
  const qs = p.toString();
  return getJSON<{ assets: LedgerAsset[]; total: number; page: number; page_size: number }>(
    `/api/ledger/customers/${encodeURIComponent(customerId)}/assets${qs ? `?${qs}` : ""}`
  );
};

export const getAssetState = (assetId: string) =>
  getJSON<{ asset_id: string; state: Record<string, LedgerObservation> }>(
    `/api/ledger/assets/${encodeURIComponent(assetId)}/state`
  ).then((d) => d.state);

export const getAttributeHistory = (assetId: string, attr: string) =>
  getJSON<{ asset_id: string; attribute: string; history: LedgerObservation[] }>(
    `/api/ledger/assets/${encodeURIComponent(assetId)}/history?attr=${encodeURIComponent(attr)}`
  ).then((d) => d.history);

export const getSourceLineage = (sourceId: string) =>
  getJSON<{ source: LedgerSource; observations: LedgerObservation[] }>(
    `/api/ledger/sources/${encodeURIComponent(sourceId)}/lineage`
  );

export const getReview = (customerId: string, opts: { type?: string; needsReview?: boolean } = {}) => {
  const p = new URLSearchParams();
  if (opts.type) p.set("type", opts.type);
  if (opts.needsReview) p.set("needs_review", "1");
  const qs = p.toString();
  return getJSON<{ review: LedgerReviewRow[]; total: number }>(
    `/api/ledger/customers/${encodeURIComponent(customerId)}/review${qs ? `?${qs}` : ""}`
  );
};

/** Full provenance rows for ONE asset (on-demand detail) — avoids bulk-loading
 *  every observation for the whole customer just to inspect one asset. */
export const getAssetReview = (assetId: string, customerId: string) =>
  getJSON<{ asset_id: string; review: LedgerReviewRow[] }>(
    `/api/ledger/assets/${encodeURIComponent(assetId)}/review?customer=${encodeURIComponent(customerId)}`
  ).then((d) => d.review);

export type GeomClass = "point" | "network" | "linear" | "areal";

export interface LedgerGeomFeature {
  asset_id: string;
  asset_type: string;
  geom_class: GeomClass;
  /** Per-asset data-trust + review summary (from /geometry; powers the roster). */
  quality_level?: string | null;
  confidence?: number | null;
  attrs?: number;
  overrides?: number;
  review?: number;
  geometry:
    | { kind: "point"; coords: [number, number] }
    | { kind: "line"; coords: [number, number][] }
    | { kind: "polygon"; coords: [number, number][]; holes: [number, number][][] };
}

export const getGeometry = (customerId: string, type?: string, epsg?: number) => {
  const p = new URLSearchParams();
  if (type) p.set("type", type);
  if (epsg) p.set("epsg", String(epsg));
  const qs = p.toString();
  return getJSON<{ features: LedgerGeomFeature[]; epsg: number }>(
    `/api/ledger/customers/${encodeURIComponent(customerId)}/geometry${qs ? `?${qs}` : ""}`
  );
};

export interface LedgerAttrSchema {
  display_name: string;
  property_group: string | null;
  unit: string | null;
  data_type: string;
  cardinality: string;
  volatility: string;
  origin: string;
}

export const getAssetSchema = (assetType: string) =>
  getJSON<{ asset_type: string; attributes: Record<string, LedgerAttrSchema> }>(
    `/api/ledger/schema?asset_type=${encodeURIComponent(assetType)}`
  ).then((d) => d.attributes);

export interface ReviewDecision {
  asset_id: string;
  attribute: string;
  action: "confirm" | "override";
  override_value?: string | null;
  notes?: string;
}

export interface DecisionResult {
  confirmed: number;
  overridden: number;
  skipped: number;
  source_id: string | null;
}

/** The confirm/override write-back — records sticky manual_override (QL-A). */
export const applyReviewDecisions = (
  customerId: string,
  assetType: string,
  decisions: ReviewDecision[],
  reviewer?: string
): Promise<DecisionResult> =>
  fetch(`${API_URL}/api/ledger/customers/${encodeURIComponent(customerId)}/review-decisions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ asset_type: assetType, reviewer, decisions }),
  }).then((res) => {
    if (!res.ok) throw new Error(`review-decisions → HTTP ${res.status}`);
    return res.json();
  });

export interface RevertResult {
  reverted: number;
  skipped: number;
  source_id: string | null;
}

/** Revert engineer overrides so the engine's reconstructed value wins again
 *  (manual_revert un-sticks the manual_override; genesis-valued attrs re-promote,
 *  driver overrides get a NULL tombstone the reconstruction skips). */
export const revertToReconstructed = (
  customerId: string,
  assetType: string,
  reverts: { asset_id: string; attribute: string; notes?: string }[],
  reviewer?: string
): Promise<RevertResult> =>
  fetch(`${API_URL}/api/ledger/customers/${encodeURIComponent(customerId)}/reverts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ asset_type: assetType, reviewer, reverts }),
  }).then((res) => {
    if (!res.ok) throw new Error(`reverts → HTTP ${res.status}`);
    return res.json();
  });

export interface RevisionResult {
  revised: number;
  skipped: number;
  source_id: string | null;
  cohort?: number;
}

/** Provenance-tracked confidence revision for ONE asset attribute (value kept;
 *  sticky manual_revision, QL auto-derived from confidence if omitted). */
export const reviseConfidence = (
  assetId: string,
  body: { customer: string; asset_type: string; attribute: string; confidence: number; quality_level?: string; reviewer?: string; notes?: string }
): Promise<RevisionResult> =>
  fetch(`${API_URL}/api/ledger/assets/${encodeURIComponent(assetId)}/confidence`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) throw new Error(`confidence → HTTP ${res.status}`);
    return res.json();
  });

/** Same, across a cohort: every asset of asset_type that carries the attribute. */
export const reviseConfidenceCohort = (
  customerId: string,
  body: { asset_type: string; attribute: string; confidence: number; quality_level?: string; reviewer?: string; notes?: string }
): Promise<RevisionResult> =>
  fetch(`${API_URL}/api/ledger/customers/${encodeURIComponent(customerId)}/confidence-revision`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) throw new Error(`confidence-revision → HTTP ${res.status}`);
    return res.json();
  });

export interface ConflictCandidate {
  value: unknown;
  source_name: string;
  source_type: string;
  confidence: number | null;
  quality_level: string | null;
  is_current: boolean;
}

/** A (asset, attribute) where sources disagree (Phase D). Resolve via override. */
export interface LedgerConflict {
  asset_id: string;
  asset_type: string;
  attribute: string;
  display_name: string | null;
  property_group: string | null;
  unit: string | null;
  candidates: ConflictCandidate[];
}

export const getConflicts = (customerId: string, type?: string) =>
  getJSON<{ conflicts: LedgerConflict[]; total: number }>(
    `/api/ledger/customers/${encodeURIComponent(customerId)}/conflicts${type ? `?type=${encodeURIComponent(type)}` : ""}`
  );

/** "Apply to similar": resolve every open conflict on (asset_type, attribute) by
 *  preferring one source. Optionally capture a reusable rule (save_rule + scope). */
export const resolveConflictsCohort = (
  customerId: string,
  body: { asset_type: string; attribute: string; source_type: string; note?: string; reviewer?: string; save_rule?: boolean; scope?: string }
) =>
  fetch(`${API_URL}/api/ledger/customers/${encodeURIComponent(customerId)}/resolve-conflicts`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) throw new Error(`resolve-conflicts → HTTP ${res.status}`);
    return res.json();
  });

/** A captured conflict-resolution rule (resolve once, applies forever). */
export interface LedgerRule {
  rule_id: number;
  customer_id: string;
  scope: string;
  asset_type: string;
  attribute: string;
  prefer_source_type: string;
  note: string | null;
  authored_by: string | null;
  authored_at: string | null;
  applied_count: number;
}

export const listRules = (customerId: string) =>
  getJSON<{ rules: LedgerRule[] }>(`/api/ledger/customers/${encodeURIComponent(customerId)}/rules`)
    .then((d) => d.rules);

/** Re-run the customer's rules against current conflicts (auto-resolve). */
export const applyRules = (customerId: string) =>
  fetch(`${API_URL}/api/ledger/customers/${encodeURIComponent(customerId)}/apply-rules`, { method: "POST" })
    .then((res) => {
      if (!res.ok) throw new Error(`apply-rules → HTTP ${res.status}`);
      return res.json() as Promise<{ resolved: number; rules_applied: unknown[] }>;
    });

/** A schema suggestion (Phase D D4) — split a conflated attribute or add a derived delta. */
export interface SchemaSuggestion {
  id: string;
  kind: "split" | "derived";
  asset_type: string;
  attribute: string;
  pattern_signal: string;
  affected_count: number;
  source_types?: string[];
  proposal: { into?: string[]; derived_name?: string; derived_from?: [string, string] };
}

export const getSchemaSuggestions = (customerId: string) =>
  getJSON<{ suggestions: SchemaSuggestion[] }>(
    `/api/ledger/customers/${encodeURIComponent(customerId)}/schema-suggestions`
  ).then((d) => d.suggestions);

export const applySchemaSuggestion = (
  customerId: string,
  body: { kind: string; asset_type: string; attribute?: string; derived_from?: [string, string]; derived_name?: string }
) =>
  fetch(`${API_URL}/api/ledger/customers/${encodeURIComponent(customerId)}/apply-schema-suggestion`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) throw new Error(`apply-schema-suggestion → HTTP ${res.status}`);
    return res.json();
  });

/** Gated IFC export (Phase D D5): rejects (throws) while open conflicts remain;
 *  returns the export-ready summary when the board is clean. */
export const exportLedgerIfc = (customerId: string, type?: string) =>
  fetch(`${API_URL}/api/ledger/customers/${encodeURIComponent(customerId)}/export${type ? `?type=${encodeURIComponent(type)}` : ""}`, { method: "POST" })
    .then(async (res) => {
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `export → HTTP ${res.status}`);
      return d as { blocked: boolean; assets: number; by_type: Record<string, number>; message: string };
    });
